// Optional opt-in to publishing this participant's transcripts, asked ONCE at the end of a
// completed 10-test set.
//
// WHY IT IS ASKED HERE AND NOT AT ENTRY. The entry consent gate is what stands between a
// recruitment email and a completed session, and every extra ask there costs conversion. This
// costs nothing by comparison: the session is already finished and the data already captured
// whichever way they answer. It is also better-informed consent — they now know what they
// actually said, rather than what they might say.
//
// WHY IT IS SEPARATE FROM THE ENTRY CONSENT. The entry tick covers recording, transcription and
// research use. It says nothing about PUBLICATION of the transcript or about verbatim quotation,
// and bundling those into it would make the agreement to each unverifiable. This is a single,
// specific, freely-declinable request covering both.
//
// WHY BOTH OUTCOMES ARE RECORDED. A "no" is written just as a "yes" is. Declining must be a
// recorded fact rather than an absence, because the research protocol (§9.1) commits to comparing
// consenters against non-consenters on the derived measures and publishing that comparison — an
// opt-in subset is self-selected, and the only honest way to release it is to quantify the
// selection rather than assume it away. That comparison is impossible if "no" looks like "never
// asked".
//
// Absence of a record therefore means NOT ASKED or ABANDONED — never "declined". Both are treated
// as no-consent downstream, but they are different facts and must stay distinguishable.
//
// Governs: transcript publication (protocol §9.1 tier B) and verbatim quotation (§9.2).
// Ethics: City St George's ETH2627-0024, P5 items 7-8.

import { useState } from 'react'
import { submitSurvey } from './survey'

export const PUBLISH_CONSENT_SURVEY_ID = 'labs-publish-consent-v1'

export const PublishConsent = ({
  sessionId,
  gameType,
  onDone,
}: {
  sessionId: string
  gameType: string // 'labs-24' | 'labs-rebus' | 'labs-cryptic'
  onDone: () => void
}) => {
  const [checked, setChecked] = useState(false) // default NO — opt-in, never opt-out
  const [busy, setBusy] = useState(false)

  const finish = async (agreed: boolean) => {
    if (busy) return
    setBusy(true)
    try {
      await submitSurvey({
        surveyId: PUBLISH_CONSENT_SURVEY_ID,
        answers: { publishTranscripts: agreed ? 'yes' : 'no' },
        hiddenFields: { sessionId, gameType, askedAt: new Date().toISOString() },
        submittedAt: new Date().toISOString(),
      })
    } catch (err) {
      // Never block the player on this. A failed write leaves no record, which reads as
      // "not asked" — correctly, since we cannot evidence a consent we failed to store.
      // eslint-disable-next-line no-console
      console.error('[labs] publish-consent write failed', err)
    }
    onDone()
  }

  return (
    <div className="mx-auto max-w-md px-4 py-8">
      <h2 className="text-xl font-semibold">Thanks — that's the set finished</h2>

      <p className="mt-4 text-sm leading-relaxed">
        By default we will <b>not</b> publish your transcripts. We publish only analysed,
        summarised results.
      </p>
      <p className="mt-3 text-sm leading-relaxed">
        We would also like to publish a sample of transcripts, so that other researchers can check
        our work. This is entirely optional and your game is already complete either way.
      </p>

      <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 p-3">
        <input
          type="checkbox"
          className="mt-1 h-5 w-5 accent-[#820458]"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
        />
        <span className="text-sm leading-relaxed">
          I'm happy for my transcripts to be published as part of a research dataset, and quoted in
          research write-ups, without my name, email or any other identifying information.
        </span>
      </label>

      <p className="mt-3 text-xs leading-relaxed text-gray-500">
        We check transcripts before publishing and leave out anything that could identify someone.
        If you'd rather not, just continue — it makes no difference to your game or your results.
      </p>

      <button
        type="button"
        disabled={busy}
        onClick={() => finish(checked)}
        className="mt-5 w-full rounded-lg bg-[#820458] py-3 font-semibold text-white disabled:opacity-40"
      >
        Continue
      </button>
    </div>
  )
}
